import { RefreshCw, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "../../../components/Spinner";
import { apiRequest } from "../../../lib/api";
import { formatTimestamp, money, relativeTime } from "../../../lib/format";
import { useInvalidateAll, useKuspitContracts } from "../../../lib/queries";
import type { KuspitContract } from "../../../types";
import { EmptyRow, Feedback, PageError, Pill, useAction } from "../shared";
import { KuspitFrame } from "./KuspitShared";

const VIEWS = [
  { value: "", label: "Todos" }, { value: "failed", label: "Sin saldo de Kuspit" }, { value: "stuck", label: "Esperando contrato" },
  { value: "stale", label: "Desactualizados" }, { value: "unlinked", label: "Sin vincular" }
];

/** Every wallet with its Kuspit contract, CLABE, token state and the balance Kuspit last reported. */
export function AdminKuspitContractsPage() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") ?? "");
  useEffect(() => { setSearch(params.get("q") ?? ""); }, [params]);
  const only = params.get("solo") ?? "";
  const contracts = useKuspitContracts({ q: search.trim() || undefined, only: only || undefined });
  const setView = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("solo", value); else next.delete("solo");
    setParams(next);
  };
  return (
    <KuspitFrame title="Contratos Kuspit" subtitle="Un renglón por wallet: contrato, CLABE, token y el saldo que reporta Kuspit.">
      <div className="discover-toolbar">
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nombre, correo, contrato, CLABE o idExterno" aria-label="Buscar contratos" />
        </label>
        <div className="chip-row" role="tablist" aria-label="Filtrar contratos">
          {VIEWS.map((view) => (
            <button key={view.value || "all"} type="button" role="tab" aria-selected={only === view.value} className={`chip${only === view.value ? " active" : ""}`} onClick={() => setView(view.value)}>{view.label}</button>
          ))}
        </div>
        <span className="toolbar-count">{contracts.data?.length ?? 0} renglones</span>
      </div>
      <p className="page-note">El saldo que ve el cliente es el que reporta Kuspit: el dinero entra por SPEI a la CLABE del contrato y sale por Kuspit, así que no hay un saldo local que comparar. <strong>Sin saldo de Kuspit</strong> significa que la última consulta falló; el cliente sigue viendo el último saldo reportado, con su fecha.</p>
      {contracts.isLoading && <div className="page-state"><Spinner label="Cargando contratos" /></div>}
      {contracts.isError && <PageError title="No pudimos cargar los contratos" retry={() => void contracts.refetch()} />}
      {contracts.data && (
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Cliente</th><th>Wallet</th><th>Contrato · CLABE</th><th>Identidad</th><th>Saldo en Kuspit</th><th>Lectura del saldo</th><th>Acciones</th></tr>
            </thead>
            <tbody>
              {contracts.data.map((row) => <ContractRow key={`${row.owner.id}-${row.wallet?.id ?? "none"}`} row={row} />)}
              {contracts.data.length === 0 && <EmptyRow colSpan={7}>Sin resultados{only ? " para este filtro" : ""}.</EmptyRow>}
            </tbody>
          </table>
        </div>
      )}
    </KuspitFrame>
  );
}

function ContractRow({ row }: { row: KuspitContract }) {
  const { owner, identity, wallet } = row;
  const action = useAction();
  const invalidate = useInvalidateAll();
  const tokenState = identity?.tokenExpiresAt
    ? new Date(identity.tokenExpiresAt).getTime() < Date.now() ? "vencido" : new Date(identity.tokenExpiresAt).getTime() < Date.now() + 2 * 3_600_000 ? "por renovar" : "vigente"
    : "sin token";
  const synchronize = () => wallet && action.run("sync", async () => {
    const result = await apiRequest<{ data: { providerBalance: number; asOf: string; changed: boolean; interestPosted: number } }>(`/api/v1/admin/kuspit/wallets/${wallet.id}/synchronize`, { method: "POST" });
    await invalidate();
    return `Kuspit reporta ${money.format(result.data.providerBalance)} al ${formatTimestamp(result.data.asOf)}${result.data.changed ? "" : " (sin cambios)"}${result.data.interestPosted > 0 ? ` · ${money.format(result.data.interestPosted)} de rendimiento registrado` : ""}.`;
  });
  const provision = () => wallet && action.run("provision", async () => {
    const result = await apiRequest<{ data: { status: string; accountRef: string; clabe: string | null } }>(`/api/v1/admin/kuspit/wallets/${wallet.id}/provision`, { method: "POST" });
    await invalidate();
    return result.data.status === "active" ? `Contrato ${result.data.accountRef} abierto${result.data.clabe ? ` · CLABE ${result.data.clabe}` : ""}.` : `Kuspit registró la solicitud; el contrato ${result.data.accountRef} sigue pendiente de autorización.`;
  });
  const relogin = () => action.run("relogin", async () => {
    const result = await apiRequest<{ data: { contrato: string | null; clabe: string | null; clabeRecorded: boolean } }>(`/api/v1/admin/kuspit/identities/${owner.id}/relogin`, { method: "POST" });
    await invalidate();
    return result.data.contrato ? `Kuspit reporta el contrato ${result.data.contrato}${result.data.clabe ? ` con CLABE ${result.data.clabe}` : ""}${result.data.clabeRecorded ? " (registrada en la wallet)" : ""}.` : "Revinculación exitosa; Kuspit aún no reporta un contrato para este cliente.";
  });
  const failed = wallet?.sync.status === "failed";
  return (
    <>
      <tr className={failed ? "row-bad" : undefined}>
        <td><Link to={`/admin/usuarios/${owner.id}`} className="table-primary"><strong>{owner.fullName}</strong><span>{owner.email}</span></Link></td>
        <td>{wallet ? <div className="table-primary"><strong>{wallet.product === "libre" ? "Wallet Libre" : "Wallet Aventuria"}</strong><span><Pill status={wallet.status ?? undefined} /></span></div> : <span className="muted">Sin wallet</span>}</td>
        <td>
          {wallet?.accountRef || identity?.contrato ? (
            <div className="table-primary">
              <strong className="mono" translate="no">{wallet?.accountRef ?? identity?.contrato}</strong>
              <span className="mono" translate="no">{wallet?.clabe ?? "sin CLABE"}</span>
            </div>
          ) : <span className="muted">{wallet ? `Pendiente · ${wallet.providerAttempts} intento(s)` : "—"}</span>}
        </td>
        <td>
          {identity ? (
            <div className="table-primary">
              <span className="mono" translate="no">idExterno {identity.idExterno}{identity.usuario ? ` · usr ${identity.usuario}` : ""}</span>
              <span><Pill tone={tokenState === "vigente" ? "ok" : tokenState === "por renovar" ? "warn" : "bad"} label={`token ${tokenState}`} /></span>
            </div>
          ) : <Pill tone="muted" label="sin vincular" />}
        </td>
        <td>
          {wallet?.balance ? (
            <div className="table-primary">
              <strong translate="no">{money.format(wallet.balance.available)}</strong>
              <span translate="no">{wallet.balance.accruedInterest > 0 ? `${money.format(wallet.balance.accruedInterest)} rendimiento · ` : ""}{wallet.balance.asOf ? `al ${formatTimestamp(wallet.balance.asOf)}` : ""}</span>
            </div>
          ) : <span className="muted">{wallet?.accountRef ? "Kuspit no ha reportado saldo" : "—"}</span>}
        </td>
        <td>
          {wallet?.accountRef ? (
            <div className="table-primary" title={wallet.sync.lastError ?? undefined}>
              <span><Pill status={wallet.sync.status} /></span>
              <span>
                {wallet.sync.status === "failed" ? <span className="mono">{wallet.sync.lastError}</span> : null}
                {wallet.sync.status === "failed" && wallet.sync.lastAttemptAt ? ` · ${relativeTime(wallet.sync.lastAttemptAt)}` : null}
                {wallet.sync.status !== "failed" && wallet.sync.lastSyncedAt ? relativeTime(wallet.sync.lastSyncedAt) : null}
              </span>
            </div>
          ) : wallet ? <span className="muted">Sin contrato</span> : "—"}
        </td>
        <td>
          <div className="row-actions">
            {wallet?.accountRef && wallet.status !== "closed" && (
              <button type="button" className={`secondary-button small${failed ? " primary" : ""}`} onClick={() => void synchronize()} disabled={action.busy !== null} title="Consultar el saldo en Kuspit ahora">
                {action.busy === "sync" ? <Spinner /> : <RefreshCw size={13} aria-hidden="true" />} Actualizar saldo
              </button>
            )}
            {wallet && !wallet.accountRef && wallet.status !== "closed" && (
              <button type="button" className="secondary-button small" onClick={() => void provision()} disabled={action.busy !== null} title="Reintentar la apertura del contrato">
                {action.busy === "provision" ? <Spinner /> : null} Abrir contrato
              </button>
            )}
            {identity && (
              <button type="button" className="link-button" onClick={() => void relogin()} disabled={action.busy !== null} title="Volver a iniciar sesión en Kuspit para descubrir contrato y CLABE">
                {action.busy === "relogin" ? "Revinculando…" : "Revincular"}
              </button>
            )}
          </div>
        </td>
      </tr>
      {(action.error || action.notice) && (
        <tr className="row-feedback"><td colSpan={7}><Feedback error={action.error} notice={action.notice} />{wallet?.sync.lastSyncedAt && <span className="field-hint">Última lectura exitosa {formatTimestamp(wallet.sync.lastSyncedAt)}</span>}</td></tr>
      )}
    </>
  );
}
